import { getBrowserAuthUser } from "@/lib/auth/api";
import { getEventsByIds } from "@/lib/events/api";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  attachmentTypeFromMime,
  FEED_PAGE_SIZE,
  isReactionType,
  POST_ATTACHMENT_BUCKET,
  resolvedMimeType,
  type CreatePostAttachmentInput,
  type CreatePollInput,
  type CurrentUser,
  type FeedAttachment,
  type FeedComment,
  type FeedCommunity,
  type FeedCursor,
  type FeedFilter,
  type FeedPage,
  type FeedPoll,
  type FeedPost,
  type HomeUnseenPostCounts,
  type PollOption,
  type ReactionType,
  type PostType,
} from "@/lib/feed/types";
import { parseLinkPreview, type LinkPreview } from "@/lib/feed/link-preview";
import { asArray, asObject, createId, getErrorMessage, safeStorageExtension } from "@/lib/utils";

type ProfileRow = {
  id: string;
  full_name: string;
  avatar_path: string | null;
  professional_title: string | null;
  occupation: string | null;
  practice_members?: {
    is_primary: boolean;
    status: string;
    practice: { id: string; name: string } | { id: string; name: string }[] | null;
  }[];
};

type PostRow = {
  id: string;
  author_id: string;
  post_type: PostType;
  audience: FeedPost["audience"];
  content: string | null;
  metadata: unknown;
  comments_enabled: boolean;
  is_pinned: boolean;
  is_hidden: boolean;
  published_at: string;
  edited_at: string | null;
  author: ProfileRow | ProfileRow[] | null;
  post_communities: {
    community: FeedCommunity | FeedCommunity[] | null;
  }[];
  post_attachments: {
    id: string;
    attachment_type: string;
    storage_bucket: string | null;
    storage_path: string | null;
    file_name: string | null;
    mime_type: string | null;
    size_bytes: number | null;
    sort_order: number;
  }[];
  post_saves?: { user_id: string; created_at?: string }[];
  shared_event_id: string | null;
};

type FeedPageIdRow = {
  post_id: string;
  cursor_rank: number;
  cursor_at: string;
};

type FeedMetricRow = {
  post_id: string;
  comment_count: number;
  like_count: number;
  useful_count: number;
  interesting_count: number;
  agree_count: number;
  my_reaction: string | null;
};

type PollResultRow = {
  post_id: string;
  total_voters: number;
  option_counts: unknown;
  other_count: number;
  selected_option_ids: string[] | null;
  selected_other_text: string | null;
};

function linkPreviewFromRow(row: PostRow): LinkPreview | null {
  if (!row.metadata || typeof row.metadata !== "object") {
    return null;
  }

  const metadata = row.metadata as { link?: unknown };
  return parseLinkPreview(metadata.link);
}

function pollFromRow(
  row: PostRow,
  result: PollResultRow | undefined,
): FeedPoll | null {
  if (row.post_type !== "poll" || !row.metadata || typeof row.metadata !== "object") {
    return null;
  }

  const metadata = row.metadata as { poll?: unknown };
  if (!metadata.poll || typeof metadata.poll !== "object") {
    return null;
  }

  const poll = metadata.poll as {
    options?: unknown;
    allow_other?: unknown;
    allow_multiple?: unknown;
  };
  if (!Array.isArray(poll.options)) {
    return null;
  }

  const options = poll.options.flatMap((value): PollOption[] => {
    if (!value || typeof value !== "object") {
      return [];
    }
    const option = value as { id?: unknown; label?: unknown };
    return typeof option.id === "string" && typeof option.label === "string"
      ? [{ id: option.id, label: option.label }]
      : [];
  });
  if (options.length < 2) {
    return null;
  }

  const rawCounts =
    result?.option_counts &&
    typeof result.option_counts === "object" &&
    !Array.isArray(result.option_counts)
      ? (result.option_counts as Record<string, unknown>)
      : {};
  const optionCounts: Record<string, number> = {};
  for (const option of options) {
    optionCounts[option.id] =
      typeof rawCounts[option.id] === "number"
        ? (rawCounts[option.id] as number)
        : 0;
  }

  return {
    options,
    allowOther: poll.allow_other === true,
    allowMultiple: poll.allow_multiple === true,
    totalVoters: Number(result?.total_voters ?? 0),
    optionCounts,
    otherCount: Number(result?.other_count ?? 0),
    selectedOptionIds: result?.selected_option_ids ?? [],
    selectedOtherText: result?.selected_other_text ?? null,
  };
}

function mapAuthor(row: ProfileRow | null): FeedAuthorFallback {
  if (!row) {
    return {
      id: "unknown",
      fullName: "Ismeretlen felhasználó",
      professionalTitle: null,
      occupation: null,
      practiceId: null,
      practiceName: null,
      avatarPath: null,
    };
  }

  const primaryMembership = asArray(row.practice_members).find(
    (membership) => membership.is_primary && membership.status === "active",
  );

  return {
    id: row.id,
    fullName: row.full_name,
    professionalTitle: row.professional_title,
    occupation: row.occupation,
    practiceId: asObject(primaryMembership?.practice)?.id ?? null,
    practiceName: asObject(primaryMembership?.practice)?.name ?? null,
    avatarPath: row.avatar_path,
  };
}

type FeedAuthorFallback = FeedPost["author"];

async function signAttachmentUrls(
  attachments: Omit<FeedAttachment, "url">[],
): Promise<FeedAttachment[]> {
  const supabase = getSupabaseBrowserClient();
  const byBucket = new Map<string, string[]>();
  for (const attachment of attachments) {
    if (!attachment.bucket || !attachment.path) {
      continue;
    }
    byBucket.set(attachment.bucket, [
      ...(byBucket.get(attachment.bucket) ?? []),
      attachment.path,
    ]);
  }

  const signedUrls = new Map<string, string>();
  await Promise.all(
    [...byBucket.entries()].map(async ([bucket, paths]) => {
      const { data } = await supabase.storage
        .from(bucket)
        .createSignedUrls([...new Set(paths)], 60 * 60);

      for (const item of data ?? []) {
        if (item.path && item.signedUrl) {
          signedUrls.set(`${bucket}:${item.path}`, item.signedUrl);
        }
      }
    }),
  );

  return attachments.map((attachment) => ({
    ...attachment,
    url:
      attachment.bucket && attachment.path
        ? signedUrls.get(`${attachment.bucket}:${attachment.path}`) ?? null
        : null,
  }));
}

export async function getCurrentUser(): Promise<CurrentUser | null> {
  const supabase = getSupabaseBrowserClient();
  const user = await getBrowserAuthUser();

  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from("profiles")
    .select(
      "id, full_name, professional_title, occupation, avatar_path, account_status, onboarding_completed, introduction_services_viewed, introduction_settings_completed, introduction_skipped, created_at",
    )
    .eq("id", user.id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      id: user.id,
      fullName: user.email ?? "Felhasználó",
      professionalTitle: null,
      occupation: null,
      avatarPath: null,
      accountStatus: "pending",
      onboardingCompleted: false,
      introductionServicesViewed: false,
      introductionSettingsCompleted: false,
      introductionSkipped: false,
      createdAt: user.created_at,
      introductionExpired: false,
    };
  }

  return {
    id: data.id,
    fullName: data.full_name,
    professionalTitle: data.professional_title,
    occupation: data.occupation,
    avatarPath: data.avatar_path,
    accountStatus: data.account_status,
    onboardingCompleted: data.onboarding_completed,
    introductionServicesViewed: data.introduction_services_viewed,
    introductionSettingsCompleted: data.introduction_settings_completed,
    introductionSkipped: data.introduction_skipped,
    createdAt: data.created_at,
    introductionExpired:
      Date.now() - new Date(data.created_at).getTime() >=
      5 * 24 * 60 * 60 * 1000,
  };
}

export async function getMyCommunities(): Promise<FeedCommunity[]> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("community_members")
    .select("community:communities(id, name, slug, member_count)")
    .eq("user_id", user.id)
    .eq("status", "active");

  if (error) {
    throw error;
  }

  const communities: FeedCommunity[] = [];
  for (const row of data ?? []) {
    const community = asObject(
      row.community as
        | {
            id: string;
            name: string;
            slug: string;
            member_count?: number | null;
          }
        | {
            id: string;
            name: string;
            slug: string;
            member_count?: number | null;
          }[]
        | null,
    );
    if (!community) {
      continue;
    }
    communities.push({
      id: community.id,
      name: community.name,
      slug: community.slug,
      memberCount: community.member_count ?? undefined,
    });
  }
  return communities;
}

async function fetchFeedPostsPage(input: {
  communityId: string | null;
  savedOnly?: boolean;
  cursor?: FeedCursor;
  filter?: FeedFilter;
  postId?: string;
}): Promise<FeedPage> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }
  if (!user) {
    return { posts: [], nextCursor: null };
  }

  let hasNextPage = false;
  let pageRows: FeedPageIdRow[] = [];
  let postIds: string[] = [];

  if (input.postId) {
    postIds = [input.postId];
  } else {
    const { data: pageData, error: pageError } = await supabase.rpc(
      "get_feed_page_ids",
      {
        p_community_id: input.communityId,
        p_saved_only: input.savedOnly ?? false,
        p_before_rank: input.cursor?.rank ?? null,
        p_before_at: input.cursor?.at ?? null,
        p_before_id: input.cursor?.id ?? null,
        p_limit: FEED_PAGE_SIZE + 1,
        p_filter: input.filter ?? "all",
      },
    );

    if (pageError) {
      throw pageError;
    }

    const allPageRows = (pageData ?? []) as FeedPageIdRow[];
    hasNextPage = allPageRows.length > FEED_PAGE_SIZE;
    pageRows = allPageRows.slice(0, FEED_PAGE_SIZE);
    postIds = pageRows.map((row) => row.post_id);
  }

  if (postIds.length === 0) {
    return { posts: [], nextCursor: null };
  }

  const { data, error } = await supabase
    .from("posts")
    .select(
      `
      id,
      author_id,
      post_type,
      audience,
      content,
      metadata,
      comments_enabled,
      is_pinned,
      is_hidden,
      published_at,
      edited_at,
      author:profiles!posts_author_id_fkey (
        id,
        full_name,
        avatar_path,
        professional_title,
        occupation,
        practice_members!practice_members_user_id_fkey (
          is_primary,
          status,
          practice:practices (
            id,
            name
          )
        )
      ),
      post_communities (
        community:communities (
          id,
          name,
          slug
        )
      ),
      post_attachments (
        id,
        attachment_type,
        storage_bucket,
        storage_path,
        file_name,
        mime_type,
        size_bytes,
        sort_order
      ),
      post_saves ( user_id ),
      shared_event_id
    `,
    )
    .in("id", postIds)
    .is("deleted_at", null);

  if (error) {
    throw error;
  }

  const unorderedRows = (data ?? []) as unknown as PostRow[];
  const rowsById = new Map(unorderedRows.map((row) => [row.id, row]));
  const rows = postIds
    .map((postId) => rowsById.get(postId))
    .filter((row): row is PostRow => Boolean(row));
  const sharedEventIds = rows
    .map((row) => row.shared_event_id)
    .filter((id): id is string => Boolean(id));
  const pollPostIds = rows
    .filter((row) => row.post_type === "poll")
    .map((row) => row.id);
  const [sharedEvents, metricsResult, pollResult] = await Promise.all([
    getEventsByIds(sharedEventIds),
    supabase.rpc("get_feed_post_metrics", { p_post_ids: postIds }),
    pollPostIds.length > 0
      ? supabase.rpc("get_feed_polls", { p_post_ids: pollPostIds })
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (metricsResult.error) {
    throw metricsResult.error;
  }
  if (pollResult.error) {
    throw pollResult.error;
  }

  const sharedEventMap = new Map(sharedEvents.map((event) => [event.id, event]));
  const metricMap = new Map(
    ((metricsResult.data ?? []) as FeedMetricRow[]).map((metric) => [
      metric.post_id,
      metric,
    ]),
  );
  const pollResultMap = new Map<string, PollResultRow>();
  for (const result of (pollResult.data ?? []) as PollResultRow[]) {
    pollResultMap.set(result.post_id, result);
  }

  const posts = await Promise.all(
    rows.map(async (row) => {
      const metric = metricMap.get(row.id);
      const rawAttachments: Omit<FeedAttachment, "url">[] = asArray(
        row.post_attachments,
      )
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((attachment) => ({
          id: attachment.id,
          type:
            attachment.attachment_type === "image" ||
            attachment.attachment_type === "video"
              ? attachment.attachment_type
              : "document",
          bucket: attachment.storage_bucket,
          path: attachment.storage_path,
          fileName: attachment.file_name,
          mimeType: attachment.mime_type,
          sizeBytes: attachment.size_bytes,
        }));

      return {
        id: row.id,
        content: row.content,
        audience: row.audience,
        postType: row.post_type,
        commentsEnabled: row.comments_enabled,
        isPinned: row.is_pinned,
        isHidden: row.is_hidden,
        publishedAt: row.published_at,
        editedAt: row.edited_at,
        commentCount: Number(metric?.comment_count ?? 0),
        author: mapAuthor(asObject(row.author)),
        communities: asArray(row.post_communities)
          .map((item) => asObject(item.community))
          .filter((community): community is FeedCommunity => Boolean(community)),
        attachments: await signAttachmentUrls(rawAttachments),
        reactionCounts: {
          like: Number(metric?.like_count ?? 0),
          useful: Number(metric?.useful_count ?? 0),
          interesting: Number(metric?.interesting_count ?? 0),
          agree: Number(metric?.agree_count ?? 0),
        },
        myReaction:
          metric?.my_reaction && isReactionType(metric.my_reaction)
            ? metric.my_reaction
            : null,
        isSaved: asArray(row.post_saves).some(
          (save) => save.user_id === user.id,
        ),
        poll: pollFromRow(row, pollResultMap.get(row.id)),
        linkPreview: linkPreviewFromRow(row),
        sharedEventId: row.shared_event_id,
        sharedEvent: row.shared_event_id
          ? sharedEventMap.get(row.shared_event_id) ?? null
          : null,
      } satisfies FeedPost;
    }),
  );

  const lastPageRow = pageRows.at(-1);
  return {
    posts,
    nextCursor:
      hasNextPage && lastPageRow
        ? {
            rank: lastPageRow.cursor_rank,
            at: lastPageRow.cursor_at,
            id: lastPageRow.post_id,
          }
        : null,
  };
}

export async function getFeedPosts(
  cursor?: FeedCursor,
  filter: FeedFilter = "all",
): Promise<FeedPage> {
  return fetchFeedPostsPage({ communityId: null, cursor, filter });
}

export async function getCommunityFeedPosts(
  communityId: string,
  cursor?: FeedCursor,
): Promise<FeedPage> {
  return fetchFeedPostsPage({ communityId, cursor });
}

export async function getFeedPostById(postId: string): Promise<FeedPost | null> {
  const page = await fetchFeedPostsPage({
    communityId: null,
    postId,
  });
  return page.posts[0] ?? null;
}

export async function getSavedFeedPosts(cursor?: FeedCursor): Promise<FeedPage> {
  return fetchFeedPostsPage({ communityId: null, savedOnly: true, cursor });
}

export async function getHomeUnseenPostCounts(): Promise<HomeUnseenPostCounts> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .rpc("get_home_unseen_post_counts")
    .single();

  if (error) {
    throw error;
  }

  const row = data as {
    question_count: number;
    community_count: number;
  } | null;
  return {
    questions: Number(row?.question_count ?? 0),
    communityPosts: Number(row?.community_count ?? 0),
  };
}

export async function markFeedPostsSeen(postIds: string[]) {
  if (postIds.length === 0) {
    return;
  }

  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("mark_feed_posts_seen", {
    p_post_ids: postIds,
  });

  if (error) {
    throw error;
  }
}

export async function getPostComments(postId: string): Promise<FeedComment[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("comments")
    .select(
      `
      id,
      post_id,
      parent_comment_id,
      content,
      created_at,
      author:profiles!comments_author_id_fkey (
        id,
        full_name,
        avatar_path,
        professional_title
      )
    `,
    )
    .eq("post_id", postId)
    .eq("is_hidden", false)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => {
    const author = asObject(
      row.author as unknown as {
        id: string;
        full_name: string;
        avatar_path: string | null;
        professional_title: string | null;
      } | null,
    );

    return {
      id: row.id,
      postId: row.post_id,
      parentCommentId: row.parent_comment_id,
      content: row.content,
      createdAt: row.created_at,
      author: {
        id: author?.id ?? "unknown",
        fullName: author?.full_name ?? "Ismeretlen felhasználó",
        professionalTitle: author?.professional_title ?? null,
        avatarPath: author?.avatar_path ?? null,
      },
    } satisfies FeedComment;
  });
}

export async function uploadPostAttachments(files: File[], userId: string) {
  const supabase = getSupabaseBrowserClient();
  const uploaded: CreatePostAttachmentInput[] = [];

  for (const file of files) {
    const mimeType = resolvedMimeType(file);
    const objectPath = `${userId}/${createId()}.${safeStorageExtension(file.name)}`;
    const { error } = await supabase.storage
      .from(POST_ATTACHMENT_BUCKET)
      .upload(objectPath, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: mimeType,
      });

    if (error) {
      const cleanupError = await removeUploadedPostAttachments(uploaded);
      if (cleanupError) {
        throw new Error(
          `${getErrorMessage(error)} A részleges feltöltés takarítása is sikertelen: ${getErrorMessage(cleanupError)}`,
        );
      }
      throw error;
    }

    uploaded.push({
      attachment_type: attachmentTypeFromMime(mimeType),
      storage_bucket: POST_ATTACHMENT_BUCKET,
      storage_path: objectPath,
      file_name: file.name,
      mime_type: mimeType,
      size_bytes: file.size,
    });
  }

  return uploaded;
}

export async function removeUploadedPostAttachments(
  attachments: CreatePostAttachmentInput[],
) {
  const paths = attachments
    .filter((attachment) => attachment.storage_bucket === POST_ATTACHMENT_BUCKET)
    .map((attachment) => attachment.storage_path);

  if (paths.length === 0) {
    return null;
  }

  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage
    .from(POST_ATTACHMENT_BUCKET)
    .remove(paths);

  return error;
}

export async function createFeedPost(input: {
  content: string;
  audience: "global" | "community";
  communityId: string | null;
  postType: PostType;
  attachments: CreatePostAttachmentInput[];
  linkPreview?: LinkPreview | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_feed_post", {
    p_content: input.content,
    p_audience: input.audience,
    p_community_id: input.communityId,
    p_post_type: input.postType,
    p_attachments: input.attachments,
    p_link_preview: input.linkPreview ?? null,
  });

  if (error) {
    throw error;
  }

  return data as string;
}

export async function createPollPost(input: {
  content: string;
  audience: "global" | "community";
  communityId: string | null;
  poll: CreatePollInput;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_poll_post", {
    p_content: input.content,
    p_audience: input.audience,
    p_community_id: input.communityId,
    p_options: input.poll.options,
    p_allow_other: input.poll.allowOther,
    p_allow_multiple: input.poll.allowMultiple,
  });

  if (error) {
    throw error;
  }

  return data as string;
}

export async function updateOwnPostContent(input: {
  postId: string;
  content: string;
  linkPreview?: LinkPreview | null;
  removeLinkPreview?: boolean;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("update_own_post_content", {
    p_post_id: input.postId,
    p_content: input.content,
    p_link_preview: input.removeLinkPreview ? null : input.linkPreview ?? null,
    p_remove_link_preview: Boolean(input.removeLinkPreview),
  });

  if (error) {
    throw error;
  }

  return data as string;
}

export async function deleteOwnPost(postId: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("delete_own_post", {
    p_post_id: postId,
  });

  if (error) {
    throw error;
  }
}

export async function submitPollVote(input: {
  postId: string;
  optionIds: string[];
  otherText: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("submit_poll_vote", {
    p_post_id: input.postId,
    p_option_ids: input.optionIds,
    p_other_text: input.otherText,
  });

  if (error) {
    throw error;
  }
}

export async function setPostReaction(input: {
  postId: string;
  userId: string;
  current: ReactionType | null;
  next: ReactionType;
}) {
  const supabase = getSupabaseBrowserClient();

  if (input.current === input.next) {
    const { error } = await supabase
      .from("reactions")
      .delete()
      .eq("post_id", input.postId)
      .eq("user_id", input.userId);

    if (error) {
      throw error;
    }

    return null;
  }

  if (input.current) {
    const { error } = await supabase
      .from("reactions")
      .update({ reaction_type: input.next })
      .eq("post_id", input.postId)
      .eq("user_id", input.userId);

    if (error) {
      throw error;
    }

    return input.next;
  }

  const { error } = await supabase.from("reactions").insert({
    user_id: input.userId,
    post_id: input.postId,
    reaction_type: input.next,
  });

  if (error) {
    throw error;
  }

  return input.next;
}

export async function setPostSaved(input: {
  postId: string;
  userId: string;
  saved: boolean;
}) {
  const supabase = getSupabaseBrowserClient();

  if (input.saved) {
    const { error } = await supabase.from("post_saves").insert({
      user_id: input.userId,
      post_id: input.postId,
    });

    if (error && error.code !== "23505") {
      throw error;
    }

    return true;
  }

  const { error } = await supabase
    .from("post_saves")
    .delete()
    .eq("post_id", input.postId)
    .eq("user_id", input.userId);

  if (error) {
    throw error;
  }

  return false;
}

export async function createComment(input: {
  postId: string;
  userId: string;
  content: string;
  parentCommentId?: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("comments")
    .insert({
      post_id: input.postId,
      author_id: input.userId,
      parent_comment_id: input.parentCommentId ?? null,
      content: input.content.trim(),
    })
    .select("id")
    .single();

  if (error) {
    throw error;
  }

  return data.id as string;
}
